import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, CheckCircle, XCircle, Globe, Monitor, Download, Trash2, LogIn, UserPlus } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { apiRequest } from '../../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function LoginLogsPage() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await apiRequest(`${BACKEND_URL}/api/admin/login-logs?limit=500`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(response.data?.detail || 'Failed to fetch activity logs');
      }

      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadBackup = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/database-backup`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download backup');
      }

      const data = await response.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `smartconnections_backup_${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const parseUserAgent = (ua) => {
    if (!ua || ua === 'unknown') return { browser: 'Unknown', os: 'Unknown' };
    
    let browser = 'Unknown';
    let os = 'Unknown';

    if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
    else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Edg')) browser = 'Edge';
    else if (ua.includes('Opera') || ua.includes('OPR')) browser = 'Opera';

    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

    return { browser, os };
  };

  const getActivityIcon = (log) => {
    if (log.action === 'delete') {
      return <Trash2 className="w-4 h-4 text-red-500" />;
    }
    if (log.login_type === 'signup') {
      return <UserPlus className="w-4 h-4 text-purple-500" />;
    }
    if (log.login_type === 'admin_login') {
      return <LogIn className="w-4 h-4 text-blue-500" />;
    }
    return <LogIn className="w-4 h-4 text-green-500" />;
  };

  const getActivityType = (log) => {
    if (log.action === 'delete') {
      return (
        <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
          Delete
        </span>
      );
    }
    if (log.login_type === 'signup') {
      return (
        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
          Sign Up
        </span>
      );
    }
    if (log.login_type === 'admin_login') {
      return (
        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
          Admin Login
        </span>
      );
    }
    return (
      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
        Sign In
      </span>
    );
  };

  const getActivityDescription = (log) => {
    if (log.action === 'delete') {
      return (
        <div>
          <span className="font-medium text-gray-900">Deleted {log.entity_type}:</span>
          <span className="ml-1 text-gray-600">{log.entity_name}</span>
        </div>
      );
    }
    return (
      <div>
        <span className="font-medium text-gray-900">{log.user_name || log.email}</span>
        {log.email && log.user_name && (
          <span className="ml-1 text-gray-500 text-sm">({log.email})</span>
        )}
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: '#7D64FA' }}>Activity History</h1>
              <p className="text-gray-600 mt-1">Track logins and data changes</p>
            </div>
            <button
              onClick={handleDownloadBackup}
              disabled={downloading}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              style={{ ':hover': { color: '#7D64FA' } }}
              title="Download Full Database Backup"
            >
              <Download className={`w-5 h-5 ${downloading ? 'animate-pulse' : ''}`} />
            </button>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchLogs}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <p className="text-sm text-gray-600">
              Total Records: <span className="font-semibold">{total}</span>
            </p>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
              <p className="mt-2 text-gray-600">Loading activity logs...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No activity logs found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Type</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Details</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Status</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">IP Address</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Device</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700">Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => {
                    const { browser, os } = parseUserAgent(log.user_agent);
                    const isDeleteAction = log.action === 'delete';
                    
                    return (
                      <tr key={log.id} className={`border-b border-gray-100 hover:bg-gray-50 ${isDeleteAction ? 'bg-red-50/30' : ''}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {getActivityIcon(log)}
                            {getActivityType(log)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {getActivityDescription(log)}
                          {isDeleteAction && (
                            <div className="text-xs text-gray-400 mt-1">
                              by {log.admin_email || 'Admin'}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {isDeleteAction ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                              <Trash2 className="w-3 h-3" />
                              Deleted
                            </span>
                          ) : log.success ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              <CheckCircle className="w-3 h-3" />
                              Success
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                              <XCircle className="w-3 h-3" />
                              Failed
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {log.ip_address ? (
                            <div className="flex items-center gap-2">
                              <Globe className="w-4 h-4 text-gray-400" />
                              <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                                {log.ip_address}
                              </span>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {log.user_agent ? (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Monitor className="w-4 h-4 text-gray-400" />
                              <span>{browser} / {os}</span>
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {formatDate(log.timestamp)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
